// Coffre du site : les valeurs ne sont jamais chargées en liste ; révélées à la demande, masquées après 30 s.
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Eye, EyeOff, KeyRound, Plus, Trash2 } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { date } from '../../lib/format.ts';
import { Alerte, Bouton, Champ, ChampChoix, Chargement, Dialogue, EtatErreur, EtatVide, Panneau, useConfirmation, useNotifier } from '../../components/ui.tsx';

interface Secret { id: string; label: string; equipement: string | null; created_at: string; auteur: string | null }

function LigneSecret({ siteId, s, peutEcrire, supprimer }: { siteId: string; s: Secret; peutEcrire: boolean; supprimer: () => void }) {
  const [valeur, setValeur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const notifier = useNotifier();
  useEffect(() => { if (!valeur) return; const t = setTimeout(() => setValeur(null), 30_000); return () => clearTimeout(t); }, [valeur]);
  const reveler = async () => {
    if (valeur) { setValeur(null); return; }
    setEnvoi(true);
    try { setValeur((await api.get<{ valeur: string }>(`/api/sites/${siteId}/secrets/${s.id}/valeur`)).valeur); }
    catch (e) { notifier(messageErreur(e), true); } finally { setEnvoi(false); }
  };
  return (
    <div className="liste-element" style={{ flexWrap: 'wrap' }}>
      <KeyRound aria-hidden className="discret" />
      <span className="liste-element-corps" style={{ minWidth: 200 }}>
        <span className="liste-element-titre">{s.label}</span>
        <span className="petit discret">{s.equipement ? `${s.equipement} — ` : ''}ajouté le {date(s.created_at)}{s.auteur ? ` par ${s.auteur}` : ''}</span>
        {valeur && <code className="mono" style={{ marginTop: 6, padding: '6px 10px', background: 'var(--surface-2)', borderRadius: 6, overflowWrap: 'anywhere', userSelect: 'all' }}>{valeur}</code>}
      </span>
      <span className="groupe-btn">
        <Bouton compact chargement={envoi} icone={valeur ? <EyeOff aria-hidden /> : <Eye aria-hidden />} onClick={reveler}>{valeur ? 'Masquer' : 'Afficher'}</Bouton>
        {valeur && <Bouton compact variante="fantome" icone={<Copy aria-hidden />} onClick={() => navigator.clipboard?.writeText(valeur).then(() => notifier('Copié.'), () => notifier('Copie impossible.', true))}>Copier</Bouton>}
        {peutEcrire && <Bouton compact variante="fantome" aria-label={`Supprimer ${s.label}`} icone={<Trash2 aria-hidden />} onClick={supprimer} />}
      </span>
    </div>
  );
}

export function OngletCoffre({ siteId, equipements }: { siteId: string; equipements: { id: string; label: string }[] }) {
  const { peut, etat } = useSession();
  const qc = useQueryClient();
  const notifier = useNotifier();
  const { confirmer, element } = useConfirmation();
  const [ajout, setAjout] = useState(false);
  const q = useQuery({ queryKey: ['coffre', siteId], queryFn: () => api.get<Secret[]>(`/api/sites/${siteId}/secrets`), enabled: peut('secret.lire') });

  if (!peut('secret.lire')) return <EtatErreur erreur={{ code: 'droit_insuffisant', message: 'Votre rôle ne donne pas accès au coffre de ce site.' }} />;
  if (etat && !etat.coffre) return <Alerte ton="ambre" titre="Coffre non configuré">La clé de chiffrement de l’installation (SECRETS_MASTER_KEY) n’est pas définie : aucun secret ne peut être enregistré. Voir le guide d’exploitation.</Alerte>;
  if (q.isLoading) return <Chargement />;
  if (q.isError) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;

  const supprimer = async (s: Secret) => {
    if (!(await confirmer(`Supprimer « ${s.label} » ?`, 'La valeur sera définitivement effacée du coffre.', 'Supprimer le secret'))) return;
    try { await api.del(`/api/sites/${siteId}/secrets/${s.id}`); notifier('Secret supprimé.'); qc.invalidateQueries({ queryKey: ['coffre', siteId] }); }
    catch (e) { notifier(messageErreur(e), true); }
  };

  return (
    <Panneau serre titre="Coffre du site" actions={peut('secret.ecrire') && <Bouton compact variante="principal" icone={<Plus aria-hidden />} onClick={() => setAjout(true)}>Ajouter</Bouton>}>
      <p className="petit discret" style={{ padding: '12px 20px 0' }}>Mots de passe, clés KNX et identifiants sont chiffrés, exclus de la recherche, et chaque consultation est inscrite au journal d’audit.</p>
      {q.data!.length === 0
        ? <EtatVide titre="Coffre vide" texte="Enregistrez ici les accès aux automates, superviseurs et passerelles plutôt que dans les notes ou les documents." />
        : <div className="liste">{q.data!.map((s) => <LigneSecret key={s.id} siteId={siteId} s={s} peutEcrire={peut('secret.ecrire')} supprimer={() => supprimer(s)} />)}</div>}
      {ajout && <AjoutSecret siteId={siteId} equipements={equipements} fermer={() => setAjout(false)}
        enregistre={() => { setAjout(false); notifier('Secret enregistré.'); qc.invalidateQueries({ queryKey: ['coffre', siteId] }); }} />}
      {element}
    </Panneau>
  );
}

function AjoutSecret({ siteId, equipements, fermer, enregistre }: { siteId: string; equipements: { id: string; label: string }[]; fermer: () => void; enregistre: () => void }) {
  const [label, setLabel] = useState('');
  const [valeur, setValeur] = useState('');
  const [equip, setEquip] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const valider = async () => {
    if (!label.trim() || !valeur) { setErreur('Indiquez un libellé et une valeur.'); return; }
    setEnvoi(true);
    try { await api.post(`/api/sites/${siteId}/secrets`, { label, valeur, equipmentId: equip || null }); enregistre(); }
    catch (e) { setErreur(messageErreur(e)); } finally { setEnvoi(false); }
  };
  return (
    <Dialogue titre="Ajouter un secret" ouvert fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" chargement={envoi} onClick={valider}>Enregistrer</Bouton></>}>
      <div className="pile">
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <Champ libelle="Libellé" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="ex. Superviseur — compte administrateur" />
        <Champ libelle="Valeur" type="password" autoComplete="off" mono value={valeur} onChange={(e) => setValeur(e.target.value)} />
        <ChampChoix libelle="Équipement concerné" value={equip} onChange={(e) => setEquip(e.target.value)} vide="Aucun en particulier"
          options={equipements.map((e) => ({ valeur: e.id, libelle: e.label }))} />
      </div>
    </Dialogue>
  );
}
